import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase, getSession } from '../config/supabase';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const SupabaseAuthContext = createContext({});

export const useSupabaseAuth = () => {
    const context = useContext(SupabaseAuthContext);
    if (!context) {
        throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider');
    }
    return context;
};

/**
 * Write session data to localStorage for backward compatibility.
 * This keeps api.js interceptor, JobMatching.js, Candidates.js, etc. working
 * without changes - they read localStorage.getItem('user') / ('token').
 */
function syncToLocalStorage(session, profile) {
    if (session?.access_token) {
        const user = session.user || {};
        const userData = {
            token: session.access_token,
            user: {
                id: user.id,
                email: user.email,
                name: profile
                    ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
                    : user.user_metadata?.full_name || user.email || '',
                firstName: profile?.first_name || user.user_metadata?.first_name || '',
                lastName: profile?.last_name || user.user_metadata?.last_name || '',
                company: profile?.company || '',
                profilePic: profile?.profile_pic || profile?.avatar_url || user.user_metadata?.avatar_url || '',
                subscription: profile?.subscription || { plan: 'free', status: 'active' },
                provider: profile?.provider || 'local'
            }
        };
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('token', session.access_token);
    } else {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
    }
}

export const SupabaseAuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch user profile from profiles table
    const fetchProfile = useCallback(async (userId) => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            if (error && error.code !== 'PGRST116') {
                console.error('Error fetching profile:', error);
                return null;
            }

            return data;
        } catch (err) {
            console.error('Profile fetch error:', err);
            return null;
        }
    }, []);

    // Initialize auth state
    useEffect(() => {
        const initAuth = async () => {
            try {
                const { session: currentSession } = await getSession();

                if (currentSession?.user) {
                    setSession(currentSession);
                    setUser(currentSession.user);
                    const userProfile = await fetchProfile(currentSession.user.id);
                    setProfile(userProfile);
                    syncToLocalStorage(currentSession, userProfile);
                }
            } catch (err) {
                console.error('Auth initialization error:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        initAuth();

        // Listen for auth state changes (including token refresh)
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, newSession) => {
                console.log('Auth state change:', event);

                setSession(newSession);
                setUser(newSession?.user || null);

                if (newSession?.user) {
                    const userProfile = await fetchProfile(newSession.user.id);
                    setProfile(userProfile);
                    syncToLocalStorage(newSession, userProfile);
                } else {
                    setProfile(null);
                    syncToLocalStorage(null, null);
                }

                setLoading(false);
            }
        );

        return () => {
            subscription?.unsubscribe();
        };
    }, [fetchProfile]);

    // Login with email/password via backend bridge
    // Backend verifies bcrypt password, syncs to auth.users, returns Supabase session tokens
    const login = async (email, password) => {
        setError(null);
        setLoading(true);

        try {
            const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
            const { access_token, refresh_token } = response.data;

            if (access_token && refresh_token) {
                // Set the Supabase session from backend-provided tokens
                const { error: sessionError } = await supabase.auth.setSession({
                    access_token,
                    refresh_token
                });
                if (sessionError) throw sessionError;
            } else {
                // Fallback: backend returned custom JWT (Supabase session unavailable)
                // Store in localStorage for backward compat
                localStorage.setItem('user', JSON.stringify(response.data));
                localStorage.setItem('token', response.data.token);
            }

            return { success: true, user: response.data.user };
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message;
            setError(errorMsg);
            return { success: false, error: errorMsg };
        } finally {
            setLoading(false);
        }
    };

    // Register new user via backend bridge
    const register = async (name, email, password, company, sessionId, turnstileToken) => {
        setError(null);
        setLoading(true);

        try {
            const nameParts = (name || '').split(' ');
            const response = await axios.post(`${API_URL}/api/auth/register`, {
                email,
                password,
                firstName: nameParts[0] || '',
                lastName: nameParts.slice(1).join(' ') || '',
                company,
                sessionId,
                turnstileToken
            });

            const { access_token, refresh_token } = response.data;

            if (access_token && refresh_token) {
                const { error: sessionError } = await supabase.auth.setSession({
                    access_token,
                    refresh_token
                });
                if (sessionError) throw sessionError;
            } else {
                localStorage.setItem('user', JSON.stringify(response.data));
                localStorage.setItem('token', response.data.token);
            }

            return { success: true, user: response.data.user, token: response.data.token };
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message;
            setError(errorMsg);
            return { success: false, error: errorMsg };
        } finally {
            setLoading(false);
        }
    };

    // Login with Google via backend bridge
    const loginWithGoogle = async (credential, sessionId) => {
        setError(null);

        try {
            const response = await axios.post(`${API_URL}/api/auth/google`, { credential, sessionId });
            const { access_token, refresh_token } = response.data;

            if (access_token && refresh_token) {
                const { error: sessionError } = await supabase.auth.setSession({
                    access_token,
                    refresh_token
                });
                if (sessionError) throw sessionError;
            } else {
                localStorage.setItem('user', JSON.stringify(response.data));
                localStorage.setItem('token', response.data.token);
            }

            return { success: true, user: response.data.user, token: response.data.token };
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message;
            setError(errorMsg);
            return { success: false, error: errorMsg };
        }
    };

    // Logout
    const logout = async () => {
        setError(null);

        try {
            await supabase.auth.signOut();
            setUser(null);
            setSession(null);
            setProfile(null);
            syncToLocalStorage(null, null);
            sessionStorage.clear();
            return { success: true };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        }
    };

    // Update profile
    const updateProfile = async (updates) => {
        if (!user) return { success: false, error: 'Not authenticated' };

        try {
            const { data, error: updateError } = await supabase
                .from('profiles')
                .update(updates)
                .eq('id', user.id)
                .select()
                .single();

            if (updateError) {
                return { success: false, error: updateError.message };
            }

            setProfile(data);
            syncToLocalStorage(session, data);
            return { success: true, profile: data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    // Get access token for API calls
    const getAccessToken = () => {
        return session?.access_token || null;
    };

    // Check if user is authenticated
    const isAuthenticated = !!user && !!session;

    const value = {
        user,
        profile,
        session,
        loading,
        error,
        isAuthenticated,
        login,
        register,
        loginWithGoogle,
        logout,
        updateProfile,
        getAccessToken,
        refreshProfile: () => user && fetchProfile(user.id).then(p => {
            setProfile(p);
            syncToLocalStorage(session, p);
        })
    };

    return (
        <SupabaseAuthContext.Provider value={value}>
            {children}
        </SupabaseAuthContext.Provider>
    );
};

export default SupabaseAuthContext;
